"use client"

import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"

import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { useDisplayCurrency } from "@/contexts/DisplayCurrencyContext"
import { accountSettingsTabPath } from "@/constants/accountSettingsSidebar"
import { fetchPnlMilestones } from "@/services/milestonesApi"
import type { PnlMilestoneDefinition } from "@/types/milestones"
import {
  clampMilestoneProgress01,
  formatMilestonePercent,
  isMilestoneReached,
  milestoneProgressGradientInnerWidthPercent,
} from "@/views/Dashboard/Profile/components/pnlMilestonesUi"

import {
  dashboardChartCardClassName,
  dashboardChartCardContentClassName,
  dashboardChartCardHeaderClassName,
  dashboardMutedGrayTextClassName,
  dashboardMutedTextClassName,
} from "./dashboardChartCardShell"

function milestoneDisplayName(m: PnlMilestoneDefinition): string {
  const name = m.name?.trim()
  return name || "Milestone"
}

function MilestoneMiniCard({
  milestone,
  formatMoney,
}: {
  milestone: PnlMilestoneDefinition
  formatMoney: (value: number) => string
}): React.ReactElement {
  const reached = isMilestoneReached(milestone)
  const progressUnit = milestone.progress_unit ?? null
  const progressCurrent = milestone.progress_current ?? null
  const progressTarget = milestone.progress_target ?? null
  const progressFractionFromApi =
    typeof milestone.progress_fraction === "number" && Number.isFinite(milestone.progress_fraction)
      ? milestone.progress_fraction
      : null
  const progressFractionDerived =
    progressCurrent != null &&
    progressTarget != null &&
    typeof progressCurrent === "number" &&
    typeof progressTarget === "number" &&
    progressTarget !== 0
      ? progressCurrent / progressTarget
      : null
  const progressFractionForBar = progressFractionFromApi ?? progressFractionDerived
  const underwaterFromCurrent =
    typeof progressCurrent === "number" &&
    progressCurrent < 0 &&
    typeof progressTarget === "number" &&
    progressTarget > 0
  const progressBarValue = (() => {
    if (underwaterFromCurrent) return 0
    const fraction = progressFractionForBar
    if (fraction != null && fraction < 0) return 0
    if (fraction != null) return Math.round(clampMilestoneProgress01(fraction) * 100)
    return reached ? 100 : 0
  })()
  const gradientInnerWidthPercent = milestoneProgressGradientInnerWidthPercent(progressBarValue)
  const formatProgressValue = (value: number) =>
    progressUnit === "pct" ? formatMilestonePercent(value) : formatMoney(value)
  const thresholdLabel =
    milestone.milestone_type === "percent"
      ? formatMilestonePercent(Number(milestone.threshold_value ?? 0))
      : formatMoney(Number(milestone.threshold_value ?? 0))
  const distance = milestone.distance_to_target ?? null

  return (
    <div
      data-dashboard-milestone-mini-card=""
      className="shrink-0 rounded-md border border-border bg-card px-3 py-2.5 dark:bg-sidebar"
    >
      <div className="mb-1.5 flex min-w-0 items-start justify-between gap-2">
        <p
          className={cn(
            "min-w-0 truncate font-[family-name:var(--font-primary)] text-sm font-medium",
            dashboardMutedTextClassName
          )}
        >
          {milestoneDisplayName(milestone)}
        </p>
        {reached ? (
          <span className="shrink-0 font-[family-name:var(--font-primary)] text-xs font-medium text-[color:var(--tessera-pnl-positive)]">
            Reached
          </span>
        ) : (
          <span className={cn("shrink-0 font-[family-name:var(--font-primary)] text-xs", dashboardMutedGrayTextClassName)}>
            {thresholdLabel}
          </span>
        )}
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        data-pnl-milestone-progress-track=""
      >
        <div className="h-full overflow-hidden rounded-full transition-all" style={{ width: `${progressBarValue}%` }}>
          {reached ? (
            <div className="h-full w-full rounded-full bg-[color:var(--tessera-pnl-positive)]" />
          ) : gradientInnerWidthPercent != null ? (
            <div
              className="pnl-milestone-progress-gradient h-full rounded-full"
              data-pnl-milestone-progress-gradient=""
              style={{ width: `${gradientInnerWidthPercent}%` }}
            />
          ) : null}
        </div>
      </div>
      <div
        className={cn(
          "mt-1.5 flex items-center justify-between gap-2 font-[family-name:var(--font-primary)] text-[11px]",
          dashboardMutedGrayTextClassName
        )}
      >
        <span>
          {progressCurrent != null && progressTarget != null
            ? `${formatProgressValue(progressCurrent)} / ${formatProgressValue(progressTarget)}`
            : progressFractionForBar != null
              ? `${Math.round(clampMilestoneProgress01(progressFractionForBar) * 100)}%`
              : reached
                ? "Complete"
                : "—"}
        </span>
        {!reached && distance != null ? <span>{formatProgressValue(distance)} to go</span> : null}
      </div>
    </div>
  )
}

export interface DashboardMilestonesCardProps {
  accountCode: string
}

/**
 * Split-row dashboard widget — milestones list in a ChartCard-sized shell with
 * an internal scroll (same height band as Portfolio Growth / Win Rate Trend).
 */
export function DashboardMilestonesCard({
  accountCode,
}: DashboardMilestonesCardProps): React.ReactElement {
  const { formatMoney } = useDisplayCurrency()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [definitions, setDefinitions] = useState<PnlMilestoneDefinition[]>([])

  const load = useCallback(async () => {
    if (!accountCode) {
      setDefinitions([])
      return
    }
    setLoading(true)
    setError(false)
    try {
      const res = await fetchPnlMilestones(accountCode)
      setDefinitions(Array.isArray(res.definitions) ? res.definitions : [])
    } catch {
      setError(true)
      setDefinitions([])
    } finally {
      setLoading(false)
    }
  }, [accountCode])

  useEffect(() => {
    void load()
  }, [load])

  const visibleMilestones = useMemo(() => {
    const active = definitions.filter((m) => m.is_active && !m.is_suppressed)
    const inProgress = active.filter((m) => !isMilestoneReached(m))
    const reached = active.filter((m) => isMilestoneReached(m))
    return [...inProgress, ...reached]
  }, [definitions])

  const reachedCount = visibleMilestones.filter((m) => isMilestoneReached(m)).length
  const activeCount = visibleMilestones.length - reachedCount

  return (
    <Card
      data-statistics-shadcn-card
      data-dashboard-sidebar-surface-card=""
      data-dashboard-milestones-card=""
      className={dashboardChartCardClassName}
    >
      <CardHeader className={dashboardChartCardHeaderClassName}>
        <div className="flex w-full min-w-0 flex-col self-start">
          <div
            className={cn(
              "mb-1.5 text-sm font-medium uppercase font-[family-name:var(--font-primary)]",
              dashboardMutedTextClassName
            )}
          >
            Milestones
          </div>
          <div className={cn("text-sm font-[family-name:var(--font-primary)]", dashboardMutedGrayTextClassName)}>
            {loading
              ? "Loading…"
              : visibleMilestones.length === 0
                ? "No active milestones"
                : `${activeCount} active${reachedCount > 0 ? ` · ${reachedCount} reached` : ""}`}
          </div>
        </div>
      </CardHeader>
      <CardContent
        className={cn(dashboardChartCardContentClassName, "overflow-y-auto ps-4 pe-4")}
      >
        {!accountCode ? (
          <p className={cn("text-sm font-[family-name:var(--font-primary)]", dashboardMutedGrayTextClassName)}>
            Select a broker account to view milestones.
          </p>
        ) : loading ? (
          <div className="flex flex-col gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[68px] animate-pulse rounded-md bg-muted dark:bg-sidebar-accent" />
            ))}
          </div>
        ) : error ? (
          <div className="flex h-full flex-col items-start justify-center gap-2">
            <p className={cn("text-sm font-[family-name:var(--font-primary)]", dashboardMutedGrayTextClassName)}>
              Couldn&apos;t load milestones.
            </p>
            <button
              type="button"
              className="text-sm text-[color:var(--brand-tropical-surf)] font-[family-name:var(--font-primary)]"
              onClick={() => void load()}
            >
              Try again
            </button>
          </div>
        ) : visibleMilestones.length === 0 ? (
          <div className="flex h-full flex-col items-start justify-center gap-2">
            <p className={cn("text-sm font-[family-name:var(--font-primary)]", dashboardMutedGrayTextClassName)}>
              Set milestones in Account settings to track progress here.
            </p>
            <Link
              to={accountSettingsTabPath("milestones")}
              className="text-sm text-[color:var(--brand-tropical-surf)] underline-offset-2 hover:underline font-[family-name:var(--font-primary)]"
            >
              Open milestones settings
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-2 pb-1">
            {visibleMilestones.map((m) => (
              <MilestoneMiniCard key={m.id} milestone={m} formatMoney={formatMoney} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default DashboardMilestonesCard
